import {Button, SmallLoader, Tile, NoteIcon} from '@kit';

import React from 'react';

import {Layout} from '../../components/layout';
import pageCss from '../../components/common.module.css';
import {getApolloError} from '../../components/utils';
import {useNameSetsQuery} from '../../generated/types';
import {buildUrl, useRoute} from '../../lib/routes';
import {NotFoundSeo, SectionSeo, Seo, buildKeywords, pageJsonLd, sectionCrumbs} from '../../lib/seo';
import {NamesGenerator} from './names-generator';
import {NameGroup, NameSet, formsLabel, peoplesOf, uniqueByCode} from './generate-names';

import css from './names-page.module.css';

const namesSummary = (groups: NameGroup[]) =>
	(peoplesOf(groups).length ? peoplesOf(groups) : uniqueByCode(groups).map((group) => group.title)).join(', ');

export const NamesPage: React.FC = () => {
	const {params} = useRoute();
	const {data, loading, error} = useNameSetsQuery({ssr: true});

	const sets = data?.nameSets || [];
	const selected = sets.find((set) => set.id === params.id);

	return (
		<Layout>
			{error ? (
				<div className={pageCss.fullSizeBlock}>{getApolloError(error).message}</div>
			) : loading && !data ? (
				<div className={pageCss.fullSizeBlock}>
					<SmallLoader />
				</div>
			) : (
				<div className={css.page}>
					{selected ? <SetView set={selected} /> : <NamesIndex notFound={!!params.id} />}
					<nav className={css.species} aria-label="Наборы имён">
						{sets.map((set) => (
							<Button
								key={set.id}
								size="small"
								shape="circular"
								appearance="outline"
								route="namesSet"
								routeParams={{id: set.id}}
								checked={set.id === selected?.id}
							>
								{set.title}
							</Button>
						))}
					</nav>
					{selected ? <AllNames set={selected} /> : <SetsGrid sets={sets} />}
				</div>
			)}
		</Layout>
	);
};

const NamesIndex: React.FC<{notFound: boolean}> = ({notFound}) => (
	<>
		{notFound ? <NotFoundSeo /> : <SectionSeo section="names" headingClassName={css.title} />}
		<p className={css.lead}>
			Выберите народ, и генератор подберёт имена из книг: личные, фамилии и названия кланов, прозвища. Нажмите на
			имя, чтобы скопировать.
		</p>
	</>
);

const SetsGrid: React.FC<{sets: NameSet[]}> = ({sets}) => (
	<ul className={css.grid}>
		{sets.map((set) => (
			<li key={set.id}>
				<Tile
					route="namesSet"
					params={{id: set.id}}
					media={<NoteIcon size={24} />}
					title={`Имена ${set.genitive}`}
					description={namesSummary(set.groups)}
				/>
			</li>
		))}
	</ul>
);

const SetView: React.FC<{set: NameSet}> = ({set}) => {
	const heading = `Имена ${set.genitive}`;
	const path = buildUrl('namesSet', {id: set.id});
	const title = `Генератор имён ${set.genitive}`;
	const description = `Случайные имена ${set.genitive} для D&D 5e: ${namesSummary(set.groups).toLowerCase()}. Генератор и полный список из книг.`;

	return (
		<>
			<Seo
				title={title}
				description={description}
				canonicalPath={path}
				keywords={buildKeywords('names', `Имена ${set.genitive}`, set.title)}
				jsonLd={pageJsonLd({
					path,
					title,
					description,
					crumbs: sectionCrumbs('names', {name: heading, path}),
				})}
			/>
			<h1 className={css.title}>{heading}</h1>
			<NamesGenerator key={set.id} groups={set.groups} />
		</>
	);
};

const AllNames: React.FC<{set: NameSet}> = ({set}) => {
	const peoples = peoplesOf(set.groups);

	return (
		<section className={css.all} aria-labelledby="names-all">
			<h2 id="names-all" className={css.allTitle}>
				Все имена: {set.title}
			</h2>
			{[null, ...peoples].map((people) => {
				const groups = set.groups.filter((group) => (group.people ?? null) === people);
				if (!groups.length) return null;
				return (
					<div key={people ?? 'all'}>
						{people && <h3 className={css.groupTitle}>{people}</h3>}
						{groups.map((group) => (
							<p key={group.code} className={css.groupItems}>
								<strong>{group.title}:</strong> {group.items.map(formsLabel).join(', ')}
							</p>
						))}
					</div>
				);
			})}
		</section>
	);
};
